import { BaseFieldRenderer } from './base-field-renderer';
import { PasswordFieldJsonData, RenderOptions } from '../../models/element-types';
import { TranslateUtils } from '../../utils/translate.utils';

export class PasswordFieldRenderer extends BaseFieldRenderer<PasswordFieldJsonData> {
    protected override get defaultData(): PasswordFieldJsonData {
        return {
            id: '',
            type: 'password',
            label: TranslateUtils.PASSWORD_FIELD.LABEL,
            placeholder: TranslateUtils.PASSWORD_FIELD.PLACEHOLDER,
            description: '',
            required: false,
            hiddenLabel: false,
            crmFieldData: null,
            showPasswordToggle: false,
            showPasswordStrength: false,
            confirmation: {
                enabled: false,
                label: TranslateUtils.PASSWORD_FIELD.CONFIRMATION_LABEL,
                placeholder: TranslateUtils.PASSWORD_FIELD.CONFIRMATION_PLACEHOLDER,
                description: '',
                hiddenLabel: false
            }
        };
    }

    protected override renderContent(fieldJsonData: PasswordFieldJsonData, options: RenderOptions = {}): HTMLElement {
        const elementContent = document.createElement('div');
        elementContent.classList.add('element-content');

        // Main password field
        const fieldContainer = document.createElement('div');
        fieldContainer.classList.add('field');
        fieldContainer.classList.toggle('required', fieldJsonData.required === true);

        if (!fieldJsonData.hiddenLabel) {
            const label = this.renderLabel(fieldJsonData);
            fieldContainer.appendChild(label);
        }

        const input = this.renderInput(fieldJsonData, options);
        const inputWrapper = this.wrapInputWithToggle(input, fieldJsonData.showPasswordToggle);
        fieldContainer.appendChild(inputWrapper);

        const errorContainer = this.renderErrorContainer(fieldJsonData);
        fieldContainer.appendChild(errorContainer);

        if (fieldJsonData.showDescription) {
            const description = this.renderDescription(fieldJsonData);
            fieldContainer.appendChild(description);
        }

        elementContent.appendChild(fieldContainer);

        if (fieldJsonData.showPasswordStrength) {
            const isRuntime = !!options.runtimeContext;
            const strengthRules = this.renderPasswordStrengthRules(fieldJsonData.id, isRuntime);
            elementContent.appendChild(strengthRules);
        }

        if (fieldJsonData.confirmation?.enabled) {
            const confirmationContainer = this.renderConfirmationField(fieldJsonData);
            elementContent.appendChild(confirmationContainer);
        }

        return elementContent;
    }

    private renderConfirmationField(fieldJsonData: PasswordFieldJsonData): HTMLElement {
        const confirmation = fieldJsonData.confirmation!;

        const fieldContainer = document.createElement('div');
        fieldContainer.classList.add('field', 'password-confirmation');
        fieldContainer.classList.toggle('required', fieldJsonData.required === true);

        if (!confirmation.hiddenLabel) {
            const label = document.createElement('label');
            label.classList.add('label');
            label.setAttribute('for', `field-${fieldJsonData.id}-confirm`);
            this.applyHtmlLabel(label, confirmation.label || TranslateUtils.PASSWORD_FIELD.CONFIRMATION_LABEL);
            fieldContainer.appendChild(label);
        }

        const input = document.createElement('input');
        input.classList.add('input', 'default');
        input.type = 'password';
        input.maxLength = 500;
        input.id = `field-${fieldJsonData.id}-confirm`;
        input.name = `${fieldJsonData.id}-confirm`;
        input.dataset['confirmFor'] = fieldJsonData.id;

        if (fieldJsonData.required) {
            input.required = true;
        }
        if (confirmation.placeholder) {
            input.placeholder = confirmation.placeholder;
        }
        const plainLabel = this.getPlainLabel(confirmation.label);
        if (plainLabel) {
            input.dataset['label'] = plainLabel;
        }

        input.setAttribute('aria-required', fieldJsonData.required ? 'true' : 'false');
        input.setAttribute('aria-invalid', 'false');

        const inputWrapper = this.wrapInputWithToggle(input, fieldJsonData.showPasswordToggle);
        fieldContainer.appendChild(inputWrapper);

        if (confirmation.description) {
            const description = document.createElement('span');
            description.classList.add('description');
            description.id = `description-${fieldJsonData.id}-confirm`;
            description.textContent = confirmation.description;
            input.setAttribute('aria-describedby', `description-${fieldJsonData.id}-confirm`);
            fieldContainer.appendChild(description);
        }

        const errorContainer = document.createElement('div');
        errorContainer.classList.add('fieldJsonData-errors');
        errorContainer.id = `errors-${fieldJsonData.id}-confirm`;
        errorContainer.setAttribute('role', 'alert');
        errorContainer.setAttribute('aria-live', 'assertive');
        fieldContainer.appendChild(errorContainer);

        return fieldContainer;
    }

    private renderPasswordStrengthRules(fieldId: string, isRuntime: boolean = false): HTMLElement {
        const container = document.createElement('div');
        container.classList.add('password-strength-rules');
        if (isRuntime) {
            container.classList.add('untouched');
        }
        container.dataset['strengthFor'] = fieldId;

        const rules = [
            { key: 'length', text: TranslateUtils.PASSWORD_FIELD.STRENGTH.MIN_LENGTH },
            { key: 'case', text: TranslateUtils.PASSWORD_FIELD.STRENGTH.MIXED_CASE },
            { key: 'number', text: TranslateUtils.PASSWORD_FIELD.STRENGTH.HAS_NUMBER }
        ];

        rules.forEach(rule => {
            const ruleElement = document.createElement('div');
            ruleElement.classList.add('password-strength-rule');
            ruleElement.dataset['rule'] = rule.key;

            const icon = document.createElement('span');
            icon.classList.add('rule-icon');

            const text = document.createElement('span');
            text.classList.add('rule-text');
            text.textContent = rule.text;

            ruleElement.append(icon, text);
            container.appendChild(ruleElement);
        });

        return container;
    }

    private wrapInputWithToggle(input: HTMLElement, showToggle?: boolean): HTMLElement {
        if (!showToggle) {
            return input;
        }

        const wrapper = document.createElement('div');
        wrapper.classList.add('password-input-wrapper');
        wrapper.appendChild(input);

        const toggleButton = this.renderPasswordToggle();
        wrapper.appendChild(toggleButton);

        return wrapper;
    }

    private renderPasswordToggle(): HTMLElement {
        const toggleButton = document.createElement('button');
        toggleButton.type = 'button';
        toggleButton.classList.add('password-toggle');
        toggleButton.setAttribute('aria-label', TranslateUtils.PASSWORD_FIELD.SHOW_PASSWORD);
        toggleButton.setAttribute('tabindex', '-1');

        const iconShow = document.createElement('span');
        iconShow.classList.add('icon', 'icon-eye');
        toggleButton.appendChild(iconShow);

        const iconHide = document.createElement('span');
        iconHide.classList.add('icon', 'icon-eye-off');
        toggleButton.appendChild(iconHide);

        toggleButton.addEventListener('click', () => {
            const wrapper = toggleButton.closest('.password-input-wrapper');
            if (!wrapper) return;

            const input = wrapper.querySelector<HTMLInputElement>('.input');
            if (!input) return;

            const isPasswordVisible = input.type === 'text';

            if (isPasswordVisible) {
                input.type = 'password';
                toggleButton.classList.remove('active');
                toggleButton.setAttribute('aria-label', TranslateUtils.PASSWORD_FIELD.SHOW_PASSWORD);
            } else {
                input.type = 'text';
                toggleButton.classList.add('active');
                toggleButton.setAttribute('aria-label', TranslateUtils.PASSWORD_FIELD.HIDE_PASSWORD);
            }
        });

        return toggleButton;
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    protected renderInput(fieldJsonData: PasswordFieldJsonData, options: RenderOptions): HTMLElement {
        const input = document.createElement('input');
        input.classList.add('input', 'default');
        input.type = 'password';
        input.maxLength = 500;

        this.applyCommonAttributes(input, fieldJsonData);

        return input;
    }
}
